import { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Badge } from "primereact/badge";
import { InputNumber } from "primereact/inputnumber";
import { Divider } from "primereact/divider";
import { Toast } from "primereact/toast";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { TabView, TabPanel } from "primereact/tabview";
import ProductService from "../../services/products/products.service";
import { ProductNewModel } from "../../models/products/product-new-model";

interface SubscriptionProduct {
  productId: number;
  productName: string;
  quantity: number;
  price?: number;
  imageUrl?: string;
}

interface Props {
  visible: boolean;
  onHide: () => void;
  onConfirm: (products: SubscriptionProduct[]) => void;
  initialProducts?: SubscriptionProduct[];
}

const SubscriptionProductModal = ({ visible, onHide, onConfirm, initialProducts = [] }: Props) => {
  debugger;
  const [selectedProducts, setSelectedProducts] = useState<SubscriptionProduct[]>([]);
  const [availableProducts, setAvailableProducts] = useState<ProductNewModel[]>([]);
  const [quantities, setQuantities] = useState<{ [key: number]: number }>({});
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const toast = useRef<Toast>(null);

  useEffect(() => {
    if (visible) {
      setSelectedProducts([...initialProducts]);
      loadAvailableProducts();
    }
  }, [visible, initialProducts]);

  const loadAvailableProducts = async () => {
    setLoading(true);
    try {
      const products = await ProductService.getAllProducts();
      setAvailableProducts(products || []);
    } catch (error) {
      showMessage("Error", "Failed to load products", "error");
    } finally {
      setLoading(false);
    }
  };

  const showMessage = (summary: string, detail: string, severity: "success" | "info" | "warn" | "error") => {
    toast.current?.show({
      severity,
      summary,
      detail,
      life: 3000,
    });
  };

  const addProduct = (product: ProductNewModel) => {
    const quantity = quantities[product.id] || 1;

    if (quantity <= 0) {
      showMessage("Invalid Quantity", "Please enter a valid quantity", "warn");
      return;
    }

    const existingIndex = selectedProducts.findIndex(p => p.productId === product.id);

    if (existingIndex >= 0) {
      const updated = [...selectedProducts];
      updated[existingIndex].quantity += quantity;
      setSelectedProducts(updated);
      showMessage("Product Updated", `Quantity updated for ${product.name}`, "success");
    } else {
      // Add new product
      const newProduct: SubscriptionProduct = {
        productId: product.id,
        productName: product.name,
        quantity,
        price: product.unitPrice,
        imageUrl: product.productImageList?.mediaUrl || process.env.PUBLIC_URL + "/assests/images/Desiri-Groundnut-Oils.png"
      };
      setSelectedProducts([...selectedProducts, newProduct]);
      showMessage("Product Added", `${product.name} added to subscription`, "success");
    }

    setQuantities(prev => ({ ...prev, [product.id]: 1 }));
  };

  const updateSelectedQuantity = (productId: number, newQuantity: number) => {
    if (newQuantity <= 0) {
      removeSelectedProduct(productId);
      return;
    }

    setSelectedProducts(prev =>
      prev.map(product =>
        product.productId === productId
          ? { ...product, quantity: newQuantity }
          : product
      )
    );
  };

  const removeSelectedProduct = (productId: number) => {
    setSelectedProducts(prev => prev.filter(product => product.productId !== productId));
    showMessage("Product Removed", "Product removed from subscription", "info");
  };

  const filteredProducts = availableProducts.filter(product =>
    product.name.toLowerCase().includes(searchText.toLowerCase())
  );

  const getTotalItems = () => selectedProducts.reduce((total, product) => total + product.quantity, 0);
  const getTotalPrice = () => selectedProducts.reduce((total, product) => total + (product.price || 0) * product.quantity, 0);

  const handleConfirm = () => {
    if (selectedProducts.length === 0) {
      showMessage("No Products", "Please add at least one product to continue", "warn");
      return;
    }
    onConfirm(selectedProducts);
    onHide();
  };

  const imageBodyTemplate = (product: ProductNewModel) => {
    const imageUrl = ( product.productImageList?.mediaUrl || process.env.PUBLIC_URL + "/assests/images/Desiri-Groundnut-Oils.png");
    return (
      <img
        src={imageUrl}
        alt={product.name}
        className="w-4rem h-4rem object-fit-cover border-round"
        onError={(e) => {
          (e.target as HTMLImageElement).src = "/assests/images/Desiri-Groundnut-Oils.png";
        }}
      />
    );
  };

  const quantityBodyTemplate = (product: ProductNewModel) => {
    return (
      <InputNumber
        value={quantities[product.id] || 1}
        onValueChange={(e) => setQuantities(prev => ({ ...prev, [product.id]: e.value || 1 }))}
        showButtons
        min={1}
        max={99}
        className="w-full"
      />
    );
  };

  const actionBodyTemplate = (product: ProductNewModel) => {
    const isAlreadySelected = selectedProducts.some(p => p.productId === product.id);
    return (
      <Button
        label={isAlreadySelected ? "Add More" : "Add"}
        icon="pi pi-plus"
        className={isAlreadySelected ? "p-button-outlined" : "p-button-success"}
        onClick={() => addProduct(product)}
        size="small"
      />
    );
  };

  const footer = (
    <div className="flex justify-content-between align-items-center w-full p-3 surface-50 border-round">
      <div className="flex align-items-center gap-3">
        <Badge
          value={`${selectedProducts.length} product${selectedProducts.length !== 1 ? 's' : ''}`}
          severity="info"
          className="text-lg"
        />
        <Badge
          value={`${getTotalItems()} total items`}
          severity="success"
          className="text-lg"
        />
        {getTotalPrice() > 0 && (
          <Badge
            value={`₹${getTotalPrice().toFixed(2)}`}
            severity="warning"
            className="text-lg"
          />
        )}
      </div>
      <div className="flex gap-2">
        <Button
          label="Cancel"
          icon="pi pi-times"
          className="p-button-secondary p-button-outlined"
          onClick={onHide}
        />
        <Button
          label="Confirm Products"
          icon="pi pi-check"
          className="p-button-success"
          onClick={handleConfirm}
          disabled={selectedProducts.length === 0}
        />
      </div>
    </div>
  );

  return (
    <>
      <Toast ref={toast} />
      <Dialog
        header={
          <div className="flex align-items-center gap-2">
            <i className="pi pi-shopping-cart text-primary"></i>
            <span>Manage Subscription Products</span>
          </div>
        }
        visible={visible}
        style={{ width: "80vw", maxWidth: "95vw", height: "80vh" }}
        onHide={onHide}
        footer={footer}
        modal
        maximizable
      >
        <TabView activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)}>
          <TabPanel
            header={
              <div className="flex align-items-center gap-2">
                <i className="pi pi-shopping-cart"></i>
                <span>Selected Products</span>
                {selectedProducts.length > 0 && (
                  <Badge value={selectedProducts.length} severity="success" />
                )}
              </div>
            }
          >
            {selectedProducts.length === 0 ? (
              <div className="text-center p-6">
                <i className="pi pi-shopping-cart text-6xl text-400 mb-3"></i>
                <div className="text-900 font-semibold mb-2">No Products Selected</div>
                <div className="text-600 mb-3">Add products from the "Add Products" tab to get started</div>
                <Button
                  label="Add Products"
                  icon="pi pi-plus"
                  onClick={() => setActiveTab(1)}
                />
              </div>
            ) : (
              <div className="grid">
                {selectedProducts.map((product) => (
                  <div key={product.productId} className="col-12 md:col-6 lg:col-4">
                    <Card className="h-full">
                      <div className="flex flex-column h-full">
                        {product.imageUrl && (
                          <div className="text-center mb-3">
                            <img
                              src={product.imageUrl}
                              alt={product.productName}
                              className="w-full h-8rem object-fit-cover border-round"
                              onError={(e) => {
                                (e.target as HTMLImageElement).src = "/assests/images/Desiri-Groundnut-Oils.png";
                              }}
                            />
                          </div>
                        )}

                        <div className="flex-1">
                          <div className="text-900 font-semibold mb-2 line-height-3">
                            {product.productName}
                          </div>

                          {product.price && (
                            <div className="text-600 mb-2">
                              ₹{product.price.toFixed(2)} per unit
                            </div>
                          )}
                        </div>

                        <Divider />

                        <div className="flex align-items-center justify-content-between">
                          <div className="flex align-items-center gap-2">
                            <span className="text-600">Qty:</span>
                            <InputNumber
                              value={product.quantity}
                              onValueChange={(e) => updateSelectedQuantity(product.productId, e.value || 0)}
                              showButtons
                              min={0}
                              max={99}
                              className="w-6rem"
                            />
                          </div>

                          <Button
                            icon="pi pi-trash"
                            className="p-button-danger p-button-text p-button-sm"
                            onClick={() => removeSelectedProduct(product.productId)}
                            tooltip="Remove product"
                          />
                        </div>

                        {product.price && (
                          <div className="mt-2 text-center">
                            <Badge
                              value={`Total: ₹${(product.price * product.quantity).toFixed(2)}`}
                              severity="success"
                            />
                          </div>
                        )}
                      </div>
                    </Card>
                  </div>
                ))}
              </div>
            )}
          </TabPanel>

          {/* Add Products Tab */}
          <TabPanel
            header={
              <div className="flex align-items-center gap-2">
                <i className="pi pi-plus"></i>
                <span>Add Products</span>
              </div>
            }
          >
            <div className="mb-3">
              <InputText
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                placeholder="Search products..."
                className="w-full"
              />
            </div>

            <DataTable
              value={filteredProducts}
              loading={loading}
              paginator
              rows={10}
              rowsPerPageOptions={[5, 10, 25]}
              className="p-datatable-sm"
              emptyMessage="No products found"
            >
              <Column
                field="media"
                header="Image"
                body={imageBodyTemplate}
                style={{ width: '100px' }}
              />
              <Column
                field="name"
                header="Product Name"
                sortable
              />
              <Column
                field="unitPrice"
                header="Price"
                body={(product) => product.unitPrice ? `₹${product.unitPrice.toFixed(2)}` : 'N/A'}
                sortable
              />
              <Column
                header="Quantity"
                body={quantityBodyTemplate}
                style={{ width: '150px' }}
              />
              <Column
                header="Action"
                body={actionBodyTemplate}
                style={{ width: '120px' }}
              />
            </DataTable>
          </TabPanel>
        </TabView>
      </Dialog>
    </>
  );
};

export { SubscriptionProductModal };
export default SubscriptionProductModal;
